/**
 * Personal Details Form
 * 
 * Collapsible form for persona_details fields:
 * - Age bracket
 * - Experience level  
 * - Role preference
 * - Orientation
 * - Relationship style
 * - Location (general)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, ChevronDown, ChevronUp, Save, Loader2, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../utils/api';

const AGE_BRACKETS = [
    { value: '', label: 'Prefer not to say' },
    { value: '18-24', label: '18-24' },
    { value: '25-34', label: '25-34' },
    { value: '35-44', label: '35-44' },
    { value: '45-54', label: '45-54' },
    { value: '55+', label: '55+' }
];

const EXPERIENCE_LEVELS = [
    { value: '', label: 'Not specified' },
    { value: 'curious', label: 'Curious — Just starting to explore' },
    { value: 'beginner', label: 'Beginner — Some experience' },
    { value: 'intermediate', label: 'Intermediate — Regular practice' },
    { value: 'advanced', label: 'Advanced — Years of experience' },
    { value: 'expert', label: 'Expert — Deep knowledge & practice' }
];

const ROLE_PREFERENCES = [
    { value: '', label: 'Not specified' },
    { value: 'dominant', label: 'Dominant' },
    { value: 'submissive', label: 'Submissive' },
    { value: 'switch', label: 'Switch' },
    { value: 'varies', label: 'Varies by context' }
];

function PersonalDetailsForm({
    personaId,
    initialData = {},
    onSave,
    collapsible = true,
    defaultExpanded = false
}) {
    const { getAuthHeaders } = useAuth();

    const [expanded, setExpanded] = useState(defaultExpanded);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);

    const [form, setForm] = useState({
        age_bracket: initialData.age_bracket || '',
        experience_level: initialData.experience_level || '',
        role_preference: initialData.role_preference || '',
        orientation: initialData.orientation || '',
        relationship_style: initialData.relationship_style || '',
        location_general: initialData.location_general || ''
    });

    // Sync with initialData when it changes
    useEffect(() => {
        setForm({
            age_bracket: initialData.age_bracket || '',
            experience_level: initialData.experience_level || '',
            role_preference: initialData.role_preference || '',
            orientation: initialData.orientation || '',
            relationship_style: initialData.relationship_style || '',
            location_general: initialData.location_general || ''
        });
    }, [initialData]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setSaved(false);
    };

    const handleSave = async () => {
        if (!personaId) return;

        setSaving(true);
        setError(null);

        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE}/api/profiles/persona/${personaId}/details`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...headers
                },
                body: JSON.stringify(form)
            });

            if (!response.ok) {
                throw new Error('Failed to save details');
            }

            setSaved(true);
            setTimeout(() => setSaved(false), 3000);

            if (onSave) {
                onSave(form);
            }
        } catch (err) {
            console.error('Save error:', err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = JSON.stringify(form) !== JSON.stringify({
        age_bracket: initialData.age_bracket || '',
        experience_level: initialData.experience_level || '',
        role_preference: initialData.role_preference || '',
        orientation: initialData.orientation || '',
        relationship_style: initialData.relationship_style || '',
        location_general: initialData.location_general || ''
    });

    const content = (
        <div className="space-y-4 pt-4">
            {/* Age Bracket */}
            <div>
                <label className="block text-xs uppercase tracking-wider text-cream/50 mb-2">
                    Age Bracket
                </label>
                <select
                    value={form.age_bracket}
                    onChange={(e) => handleChange('age_bracket', e.target.value)}
                    className="w-full bg-charcoal-950 text-cream px-3 py-2 border border-wine-700/50 focus:border-wine-500 outline-none text-sm"
                >
                    {AGE_BRACKETS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {/* Experience Level */}
            <div>
                <label className="block text-xs uppercase tracking-wider text-cream/50 mb-2">
                    Experience Level
                </label>
                <select
                    value={form.experience_level}
                    onChange={(e) => handleChange('experience_level', e.target.value)}
                    className="w-full bg-charcoal-950 text-cream px-3 py-2 border border-wine-700/50 focus:border-wine-500 outline-none text-sm"
                >
                    {EXPERIENCE_LEVELS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {/* Role Preference */}
            <div>
                <label className="block text-xs uppercase tracking-wider text-cream/50 mb-2">
                    Role Preference
                </label>
                <select
                    value={form.role_preference}
                    onChange={(e) => handleChange('role_preference', e.target.value)}
                    className="w-full bg-charcoal-950 text-cream px-3 py-2 border border-wine-700/50 focus:border-wine-500 outline-none text-sm"
                >
                    {ROLE_PREFERENCES.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {/* Orientation - freeform */}
            <div>
                <label className="block text-xs uppercase tracking-wider text-cream/50 mb-2">
                    Orientation
                </label>
                <input
                    type="text"
                    value={form.orientation}
                    onChange={(e) => handleChange('orientation', e.target.value)}
                    placeholder="e.g., pansexual, queer, heteroflexible..."
                    className="w-full bg-charcoal-950 text-cream px-3 py-2 border border-wine-700/50 focus:border-wine-500 outline-none text-sm placeholder:text-dark-500"
                />
            </div>

            {/* Relationship Style */}
            <div>
                <label className="block text-xs uppercase tracking-wider text-cream/50 mb-2">
                    Relationship Style
                </label>
                <input
                    type="text"
                    value={form.relationship_style}
                    onChange={(e) => handleChange('relationship_style', e.target.value)}
                    placeholder="e.g., poly, mono, exploring, relationship anarchist..."
                    className="w-full bg-charcoal-950 text-cream px-3 py-2 border border-wine-700/50 focus:border-wine-500 outline-none text-sm placeholder:text-dark-500"
                />
            </div>

            {/* Location */}
            <div>
                <label className="block text-xs uppercase tracking-wider text-cream/50 mb-2">
                    General Location
                </label>
                <input
                    type="text"
                    value={form.location_general}
                    onChange={(e) => handleChange('location_general', e.target.value)}
                    placeholder="City or region (optional)"
                    className="w-full bg-charcoal-950 text-cream px-3 py-2 border border-wine-700/50 focus:border-wine-500 outline-none text-sm placeholder:text-dark-500"
                />
                <p className="text-xs text-dark-400 mt-1">Only share what you're comfortable with</p>
            </div>

            {/* Error */}
            {error && (
                <div className="p-3 bg-red-900/20 border border-red-500/30 text-red-400 text-sm">
                    {error}
                </div>
            )}

            {/* Save Button */}
            <div className="pt-2">
                <button
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                    className={`
                        flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all
                        ${hasChanges
                            ? 'bg-wine-600 hover:bg-wine-500 text-white'
                            : 'bg-dark-700 text-dark-400 cursor-not-allowed'
                        }
                        ${saved ? 'bg-green-600 hover:bg-green-600' : ''}
                    `}
                >
                    {saving ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                    ) : saved ? (
                        <><Check className="w-4 h-4" /> Saved</>
                    ) : (
                        <><Save className="w-4 h-4" /> Save Details</>
                    )}
                </button>
            </div>
        </div>
    );

    if (!collapsible) {
        return (
            <div className="glass-card p-6 border border-wine-700/30">
                <h3 className="text-lg font-display font-bold text-cream flex items-center gap-2">
                    <User className="w-5 h-5 text-wine-400" />
                    Personal Details
                </h3>
                {content}
            </div>
        );
    }

    return (
        <div className="glass-card border border-wine-700/30 overflow-hidden">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-wine-950/20 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-wine-400" />
                    <span className="font-display font-bold text-cream">Personal Details</span>
                    {(form.role_preference || form.experience_level) && (
                        <span className="text-xs px-2 py-0.5 bg-wine-900/30 text-wine-300">
                            {[form.role_preference, form.experience_level].filter(Boolean).join(' · ')}
                        </span>
                    )}
                </div>
                {expanded ? (
                    <ChevronUp className="w-5 h-5 text-cream/40" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-cream/40" />
                )}
            </button>

            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="px-4 pb-4 border-t border-wine-700/20">
                            {content}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default PersonalDetailsForm;
